import clsx from "clsx";
import { forwardRef, memo, useMemo } from "react";
import { Link } from "react-router";

import { Icon } from "@atoms/icon";
import { Typography } from "@atoms/typography";
import {
  IButtonLoaderTypeEnum,
  IButtonModeEnum,
  IButtonSizeEnum,
  IButtonStyleEnum,
  IButtonVariantsEnum,
} from "@molecules/button/types";

import rootClasses from "./styles.module.scss";

import type { IButtonProps } from "@molecules/button/types";
import "ldrs/dotPulse";
import "ldrs/lineSpinner";

export const Button = memo(
  forwardRef<HTMLButtonElement, IButtonProps>(
    (
      {
        label,
        endIcon,
        startIcon,
        disabled = false,
        hasUnderline = false,
        linkTo,
        mode = IButtonModeEnum.Button,
        onClick,
        isLoading,
        onMouseDown,
        onSubmit,
        size = IButtonSizeEnum.Medium,
        variant = IButtonVariantsEnum.Primary,
        styleType = IButtonStyleEnum.Fancy,
        className,
        children,
        loaderType = IButtonLoaderTypeEnum.DotPulse,
        defaultCursor,
        ...rootAttributes
      },
      ref,
    ) => {
      const labelComponent = useMemo(
        () => (
          <Typography.Span
            fontType={"label-small"}
            themeColor={"inherit"}
            className={clsx(rootClasses.button__container)}
          >
            {startIcon && (
              <Icon
                name={startIcon}
                fontSize={"fs-20"}
                className={rootClasses.button__icon}
              />
            )}
            {label && <span className={"px-4"}>{label || undefined}</span>}
            {endIcon && (
              <Icon
                name={endIcon}
                fontSize={"fs-20"}
                className={rootClasses.button__icon}
              />
            )}
            {children}
          </Typography.Span>
        ),
        [children, endIcon, label, startIcon],
      );

      const buttonChild = useMemo(
        () => (
          <>
            {linkTo ? (
              <Link
                data-underline={hasUnderline}
                to={linkTo}
                className={clsx(rootClasses.button__link)}
              >
                {labelComponent}
              </Link>
            ) : (
              labelComponent
            )}
          </>
        ),
        [hasUnderline, labelComponent, linkTo],
      );

      const loadingContent = useMemo(() => {
        return isLoading ? (
          <Typography.Span
            fontType={"label-small"}
            themeColor={"inherit"}
            className={clsx(rootClasses.button__container)}
          >
            <div
              className={clsx(
                "d-flex justify-content-center align-items-center",
                rootClasses.button__flex0,
                loaderType === IButtonLoaderTypeEnum.DotPulse &&
                  rootClasses.button__loader,
              )}
            >
              {loaderType === IButtonLoaderTypeEnum.DotPulse && (
                <l-dot-pulse size={"30"} speed={"1.1"} />
              )}
              {loaderType === IButtonLoaderTypeEnum.Spinner && (
                <l-line-spinner size={"20"} speed={"1.1"} stroke={"2"} />
              )}
            </div>
          </Typography.Span>
        ) : (
          buttonChild
        );
      }, [isLoading, loaderType, buttonChild]);

      return (
        <button
          data-size={size}
          data-variant={variant}
          data-style={styleType}
          data-disabled={disabled}
          className={clsx(
            rootClasses.button,
            defaultCursor && rootClasses.defaultCursor,
            className,
          )}
          ref={ref}
          disabled={disabled}
          type={mode === IButtonModeEnum.Button ? "button" : "submit"}
          onMouseDown={onMouseDown}
          onClick={onClick}
          onSubmit={onSubmit}
          {...rootAttributes}
        >
          {loadingContent}
        </button>
      );
    },
  ),
);

Button.displayName = "Button";
